import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

export const SYNC_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds

export interface ApiResponse {
  since: string;
  until: string;
  results: Array<any>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private http: HttpClient) {}

  public allDocs(type: string, since?: string): Promise<ApiResponse> {
    let params = new HttpParams();
    if (since) {
      params = params.append('since', since);
      // https://github.com/angular/angular/pull/30010 don't cache in service worker
      params = params.append('ngsw-bypass', 'true');
    }
    const options = {
      ...this.httpOptions,
      params,
    };
    return firstValueFrom(
      this.http.get<ApiResponse>(`${environment.acmAPI}${type}/`, options),
    );
  }

  public async get<T>(type: string, id: string): Promise<T> {
    return firstValueFrom(
      this.http.get<T>(`${environment.acmAPI}${type}/${id}/`, this.httpOptions),
    );
  }

  /**
   * put to API endpoint
   *
   * @param {string} type
   * @param {string} id
   * @param {*} payload
   * @param {string} [at] ISO-8601 date string triggers server side conflict detection
   * @returns {Promise<any>}
   * @memberof ApiService
   */
  public async put<T>(
    type: string,
    id: string,
    payload: T,
    at?: string,
  ): Promise<T> {
    let params = new HttpParams();
    if (at !== undefined) {
      params = params.append('at', at);
    }
    const options = {
      ...this.httpOptions,
      params,
    };
    return firstValueFrom(
      this.http.put<T>(`${environment.acmAPI}${type}/${id}/`, payload, options),
    );
  }

  public async delete<T>(
    type: string,
    id: string,
    at?: string,
  ): Promise<boolean> {
    try {
      let params = new HttpParams();
      if (at !== undefined) {
        params = params.append('at', at);
      }
      const options = {
        ...this.httpOptions,
        params,
      };

      await firstValueFrom(
        this.http.delete<T>(`${environment.acmAPI}${type}/${id}/`, options),
      );
      return true;
    } catch (e) {
      if (e.status !== 409) {
        throw e;
      }
      // Conflict means doc was updated on server before our delete was sent.
      // Get the latest version and try again.
      return this.get<T>(type, id)
        .then((result: any) => {
          // got the latest version so try to delete it again
          return this.delete<T>(type, id, result.updated);
        })
        .catch((e) => {
          if (e.status !== 404) {
            throw e;
          }
          // already deleted
          console.error(`${type}/${id} already deleted`);
          return true;
        });
    }
  }
}
